<script lang="ts" setup>
import { AppButton, AppLogo, AppPassword, AppPasswordCriteria, AppTextField, AppTypography } from '@/core/components';
import type { FormErrors } from '@/core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from '../store/auth';
import type { RegisterForm, RegisterResponse } from '../types/Auth';
import SocialLoginButtons from './SocialLoginButtons.vue';

const { validations } = useValidation();

const form = ref<RegisterForm>({
  email: '',
  password: '',
  password_confirmation: '',
});

const rules = computed(() => ({
  email: {
    required: validations.required('Email'),
    email: validations.email(),
  },
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    maxLength: validations.maxLength('Password', 20),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Password Confirmation'),
    sameAs: validations.sameAs('Password Confirmation', 'Password', form.value.password),
    minLength: validations.minLength('Password Confirmation', 8),
    maxLength: validations.maxLength('Password Confirmation', 20),
  },
}));

const router = useRouter();

const { user } = storeToRefs(useAuthStore());
const v$ = useVuelidate(rules, form, { $autoDirty: true });

const { token, initToken } = await useGenerateReCaptcha('register');
const { setToken } = useAuthState();
const { getSession } = useAuth();
const { data, pending: registerLoading, execute: execSubmit } = useSubmit<{ data: RegisterResponse }>();
const registerError = ref<{ data: FormErrors<RegisterForm> }>({ data: { errors: {}, message: '' } });
const { $posthog } = useNuxtApp();

const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();

  if (!result) {
    return;
  }

  try {
    registerLoading.value = true;
    await execSubmit('auth/register', {
      method: 'POST',
      body: {
        email: form.value.email,
        password: form.value.password,
        password_confirmation: form.value.password_confirmation,
      },
      headers: {
        ...(token.value && { 'X-Recaptcha-Token': token.value }),
      },
    });

    if (data.value) {
      setToken(data.value.data.access_token);
      await getSession();
      user.value = data.value.data.user;
      $posthog?.identify(String(data.value.data.user.id), { email: data.value.data.user.email });
      $posthog?.capture('user_registered', { email: data.value.data.user.email });
      await router.push({ name: 'verify' });
    }
  } catch (error) {
    registerError.value = error as { data: FormErrors<RegisterForm> };
    await initToken();
  } finally {
    registerLoading.value = false;
  }
};

const errors = computed(() => {
  return $parsedError<RegisterForm>(form.value, v$, registerError.value?.data?.errors);
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp" class="auth-form">
    <ClientOnly>
      <AppLogo src="/logo.png" width="74px" height="74px" class="mb-12" />
      <AppTypography element="h1" type="large" class="mb-8">Sign up</AppTypography>
      <AppTextField
        v-model="form.email"
        data-cy="input-email"
        :error-messages="errors.email"
        :disabled="registerLoading"
        label="Email address"
        data-testid="email"
        class="mb-3"
        autocomplete="email"
      >
      </AppTextField>

      <AppPassword
        v-model="form.password"
        data-cy="input-password"
        label="Password"
        :error-messages="errors.password"
        :disabled="registerLoading"
        data-testid="password"
        class="mb-3"
      >
      </AppPassword>

      <AppPassword
        v-model="form.password_confirmation"
        data-cy="input-password-confirmation"
        label="Confirm password"
        data-testid="password-confirmation"
        :error-messages="errors.password_confirmation"
        :disabled="registerLoading"
        class="mb-3"
      >
      </AppPassword>

      <AppPasswordCriteria :password="form.password" class="mb-8" />

      <AppButton
        data-cy="button-submit"
        block
        class="mt-4 mb-8 font-weight-bold"
        type="submit"
        data-testid="submit"
        :loading="registerLoading"
        :disabled="registerLoading || v$.$invalid"
      >
        Sign up
      </AppButton>

      <SocialLoginButtons class="mb-8" :disabled="registerLoading" />

      <AppTypography element="span" type="headline-2" class="d-block text-decoration-none mb-8 text-center">
        By signing up, I agree to the
        <nuxt-link to="/customer-terms-and-conditions" class="font-weight-bold text-decoration-none text-primary-1">
          Terms & Conditions
        </nuxt-link>
        and
        <nuxt-link to="/privacy-policy" class="font-weight-bold text-decoration-none text-primary-1">
          Privacy Policy
        </nuxt-link>
      </AppTypography>

      <AppTypography element="span" class="d-block text-center text-decoration-none">
        Already have an account?
        <nuxt-link to="/login" class="text-primary font-weight-bold text-decoration-none text-primary-1">
          Sign in
        </nuxt-link>
      </AppTypography>
    </ClientOnly>
  </v-form>
</template>
